import { useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import ThemeToggle from "@/components/ThemeToggle";
import { Terminal, ArrowRight } from "lucide-react";

const AUTH_BG =
  "https://images.pexels.com/photos/31862217/pexels-photo-31862217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Login = () => {
  const [agreed, setAgreed] = useState(false);

  const handleGoogle = () => {
    if (!agreed) return;
    sessionStorage.setItem("ilk_accepted_terms", "true");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 flex items-center justify-center bg-teal-400 rounded-xl">
              <Terminal className="h-5 w-5 text-zinc-950" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter text-zinc-100">
              IndieLaunchkit
            </span>
          </div>
          <ThemeToggle />
        </div>

        <div className="max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400 mb-4">
            // Marketing toolkit for indie devs
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-zinc-100 leading-none">
            Ship your update.
            <br />
            <span className="text-teal-400 drop-shadow-[0_0_8px_rgba(45,212,191,0.35)]">
              We write the words.
            </span>
          </h1>
          <p className="text-zinc-400 mt-5 leading-relaxed">
            Generate Steam patch notes, changelogs, and store page copy in seconds — tuned for the
            way indie communities actually read.
          </p>

          <div className="mt-8 flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5 border-zinc-600 data-[state=checked]:bg-teal-400 data-[state=checked]:border-teal-400 data-[state=checked]:text-zinc-950 rounded-xl"
              data-testid="terms-consent-checkbox"
            />
            <label htmlFor="terms" className="text-sm text-zinc-400 leading-snug select-none">
              I agree to the{" "}
              <Link to="/terms" className="text-teal-400 hover:text-teal-300 underline underline-offset-2" data-testid="login-terms-link">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-teal-400 hover:text-teal-300 underline underline-offset-2" data-testid="login-privacy-link">
                Privacy Policy
              </Link>
              .
            </label>
          </div>

          <button
            onClick={handleGoogle}
            disabled={!agreed}
            data-testid="google-login-btn"
            className="mt-6 w-full flex items-center justify-center gap-3 bg-teal-400 text-zinc-950 font-semibold py-3 px-6 rounded-xl transition-colors duration-200 hover:bg-teal-300 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed active:scale-[0.99]"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M12.24 10.4v3.83h5.4a4.63 4.63 0 0 1-2 3.03v2.5h3.24c1.9-1.75 2.99-4.33 2.99-7.4 0-.7-.06-1.37-.18-2z"/>
              <path fill="currentColor" d="M12.24 22c2.7 0 4.96-.9 6.62-2.43l-3.24-2.5c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.58-4.12H3.3v2.58A9.99 9.99 0 0 0 12.24 22z"/>
              <path fill="currentColor" d="M6.66 13.9a5.98 5.98 0 0 1 0-3.8V7.53H3.3a10 10 0 0 0 0 8.95z"/>
              <path fill="currentColor" d="M12.24 5.98c1.47 0 2.78.5 3.82 1.5l2.86-2.86A9.98 9.98 0 0 0 3.3 7.53l3.36 2.58c.78-2.36 2.98-4.13 5.58-4.13z"/>
            </svg>
            Continue with Google
            <ArrowRight className="h-4 w-4" />
          </button>
          {!agreed && (
            <p className="mt-2 text-xs text-zinc-600 font-mono">Accept the terms to continue.</p>
          )}
        </div>

        <p className="font-mono text-xs text-zinc-600">
          Payments handled by Paddle · contact@getindielaunchkit.com
        </p>
      </div>

      {/* Right: image */}
      <div className="relative hidden lg:block">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/80" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute bottom-16 left-16 right-16 lg:right-auto lg:max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400 mb-3">
            // Essential tools. Zero writer's block.
          </p>
          <p className="font-heading text-2xl font-bold text-zinc-100 tracking-tight leading-snug">
            "Hades meets Risk of Rain" in, a wishlist-worthy store page out.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
